<template>
  <el-dialog
    title="HTML 預覽與編輯"
    :visible.sync="dialogVisible"
    width="90%"
    top="5vh"
    :modal="true"
    :modal-append-to-body="false"
    :append-to-body="true"
    :close-on-click-modal="false"
    custom-class="html-preview-dialog"
    @close="handleClose">
    <div class="preview-container">
      <el-tabs v-model="activeTab" @tab-click="handleTabClick" class="preview-tabs">
        <el-tab-pane label="預覽" name="preview">
          <div class="html-preview" v-html="htmlContent"></div>
        </el-tab-pane>
        <el-tab-pane label="編輯" name="edit">
          <el-input
            type="textarea"
            v-model="editableContent"
            :rows="25"
            placeholder="HTML內容"
            class="html-editor">
          </el-input>
        </el-tab-pane>
      </el-tabs>
    </div>
    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" @click="saveChanges">保存修改</el-button>
      <el-button type="success" @click="downloadFile">下載</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { fetchFileContent, downloadFile } from '@/utils/fileUtils'
import { Message, Loading } from 'element-ui'

export default {
  name: 'HtmlPreviewEditor',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    fileUrl: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      dialogVisible: false,
      activeTab: 'preview',
      htmlContent: '',
      editableContent: '',
      originalContent: ''
    }
  },
  watch: {
    visible: {
      handler(newVal) {
        this.dialogVisible = newVal
        if (newVal && this.fileUrl) {
          this.loadContent()
        }
      },
      immediate: true
    }
  },
  methods: {
    // 加载HTML内容
    async loadContent() {
      if (!this.fileUrl) {
        Message.warning('HTML文件不可用')
        return
      }

      try {
        const loading = Loading.service({
          lock: true,
          text: '正在載入HTML內容...',
          background: 'rgba(255, 255, 255, 0.9)'
        })

        const content = await fetchFileContent(this.fileUrl)
        this.htmlContent = content
        this.editableContent = content
        this.originalContent = content
        this.activeTab = 'preview'
        
        loading.close()
      } catch (error) {
        console.error('HTML預覽失敗:', error)
        Message.error('HTML內容載入失敗，請稍後重試')
      }
    },

    // 处理标签页切换
    handleTabClick(tab) {
      if (tab.name === 'edit') {
        // 切换到编辑模式时，同步当前内容
        this.editableContent = this.htmlContent
      } else if (tab.name === 'preview') {
        // 切换到预览模式时，更新预览内容
        this.htmlContent = this.editableContent
      }
    },

    // 保存修改
    saveChanges() {
      this.htmlContent = this.editableContent
      this.$emit('content-changed', this.editableContent)
      Message.success('HTML內容已更新')
    },

    // 下载文件
    downloadFile() {
      try {
        const filename = `edited_html_${new Date().getTime()}.html`
        const success = downloadFile(this.editableContent, filename, 'text/html')
        if (success) {
          Message.success('HTML文件下載成功')
        } else {
          Message.error('HTML文件下載失敗')
        }
      } catch (error) {
        console.error('HTML下載失敗:', error)
        Message.error('HTML文件下載失敗')
      }
    },

    // 关闭对话框
    handleClose() {
      this.dialogVisible = false
      this.$emit('update:visible', false)
    }
  }
}
</script>

<style scoped>
/* 预览对话框样式 */
.html-preview-dialog {
  border-radius: 8px;
}

.html-preview-dialog .el-dialog {
  margin: 5vh auto 0 !important;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.html-preview-dialog .el-dialog__body {
  padding: 0 !important;
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.preview-container {
  height: 70vh;
  min-height: 500px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.preview-tabs {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.preview-tabs .el-tabs__header {
  margin: 0;
  padding: 0 20px;
  border-bottom: 1px solid #e4e7ed;
  flex-shrink: 0;
}

.preview-tabs .el-tabs__content {
  flex: 1;
  overflow: hidden;
  padding: 0;
}

.preview-tabs .el-tab-pane {
  height: 100%;
  overflow: auto;
  padding: 20px;
}

/* HTML预览样式 */
.html-preview {
  border: 1px solid #e6e6e6;
  border-radius: 4px;
  padding: 20px;
  background: #fff;
  height: calc(100% - 40px);
  overflow-y: auto;
  overflow-x: auto;
  margin: 0;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  line-height: 1.6;
  /* 确保滚动条显示 */
  scrollbar-width: thin;
  scrollbar-color: #c0c4cc #f5f7fa;
}

.html-preview::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.html-preview::-webkit-scrollbar-track {
  background: #f5f7fa;
  border-radius: 4px;
}

.html-preview::-webkit-scrollbar-thumb {
  background: #c0c4cc;
  border-radius: 4px;
}

.html-preview::-webkit-scrollbar-thumb:hover {
  background: #909399;
}

.html-preview h1, .html-preview h2, .html-preview h3, 
.html-preview h4, .html-preview h5, .html-preview h6 {
  margin-top: 24px;
  margin-bottom: 16px;
  font-weight: 600;
  line-height: 1.25;
}

.html-preview h1 { border-bottom: 1px solid #eaecef; padding-bottom: 10px; }
.html-preview h2 { border-bottom: 1px solid #eaecef; padding-bottom: 8px; }

.html-preview p {
  margin-bottom: 16px;
}

.html-preview table {
  border-collapse: collapse;
  margin: 20px 0;
  width: 100%;
}

.html-preview table th,
.html-preview table td {
  border: 1px solid #dfe2e5;
  padding: 8px 12px;
  text-align: left;
}

.html-preview table th {
  background-color: #f6f8fa;
  font-weight: 600;
}

.html-preview table tr:nth-child(even) {
  background-color: #f6f8fa;
}

/* 编辑器样式 */
.html-editor {
  height: calc(100% - 40px);
  margin: 0;
}

.html-editor .el-textarea {
  height: 100%;
}

.html-editor .el-textarea__inner {
  height: 100% !important;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace !important;
  font-size: 14px !important;
  line-height: 1.5 !important;
  resize: none !important;
  border: 1px solid #e6e6e6;
  border-radius: 4px;
  /* 确保滚动条显示 */
  scrollbar-width: thin;
  scrollbar-color: #c0c4cc #f5f7fa;
}

.html-editor .el-textarea__inner::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.html-editor .el-textarea__inner::-webkit-scrollbar-track {
  background: #f5f7fa;
  border-radius: 4px;
}

.html-editor .el-textarea__inner::-webkit-scrollbar-thumb {
  background: #c0c4cc;
  border-radius: 4px;
}

.html-editor .el-textarea__inner::-webkit-scrollbar-thumb:hover {
  background: #909399;
}

.dialog-footer {
  text-align: right;
  padding: 12px 20px;
  border-top: 1px solid #e6e6e6;
}

.dialog-footer .el-button {
  margin-left: 10px;
}
</style>
